import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { liveNewsOptions } from "../../../lib/liveNews";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { MAX_PAGE_SIZE, fetchEnquiriesPage } from "../services/enquiriesService";
import { useOpenedEnquiries } from "./useOpenedEnquiries";
import { isNewEnquiry } from "../openedEnquiries";

/**
 * How many enquiries nobody has opened or moved yet — the number the rail
 * carries beside Enquiries.
 *
 * Worked out here rather than asked for: the backend counts enquiries, not
 * unread ones, because whether a record has been read is this browser's own
 * bookkeeping (see `openedEnquiries`). So a page is fetched and each row is
 * put to the same question the list's tag asks — `isNewEnquiry` — so the
 * badge and the tags can never disagree.
 *
 * One page, the largest the endpoint serves. A workspace with more than a
 * hundred enquiries nobody has ever opened is a workspace where the exact
 * number has stopped meaning anything — the badge says "99+" at that point,
 * and the alternative is walking every page of the list to put a digit on a
 * nav item.
 *
 * It is its own query rather than a read of the list's: the list is searched
 * and paginated by whatever the screen is showing, and the rail's count must
 * not move because somebody typed in a search box.
 *
 * The badge caps at `MAX_SHOWN_COUNT`, which is the rail's to draw and not
 * this hook's — what is returned here is always the true number.
 */

/** The count's own key, kept out of the paginated list's cache. */
export const NEW_ENQUIRIES_QUERY_KEY = ["enquiries", "unopened"] as const;

export function useNewEnquiryCount(): number {
  const getToken = useTokenGetter();
  const opened = useOpenedEnquiries();

  // A failure is silent: the rail is navigation, and a workspace whose count
  // could not be read is better off with no badge than with an error on it.
  const query = useQuery({
    queryKey: NEW_ENQUIRIES_QUERY_KEY,
    queryFn: ({ signal }) =>
      fetchEnquiriesPage(getToken, { page: 1, pageSize: MAX_PAGE_SIZE, signal }),
    // Polled, and stale no sooner than the poll: an enquiry that arrives while
    // the app is open is news the moment it lands rather than the next time
    // somebody reloads. See `liveNews`.
    ...liveNewsOptions,
  });

  const enquiries = query.data?.enquiries;

  return useMemo(
    () =>
      (enquiries ?? []).reduce(
        (total, enquiry) => (isNewEnquiry(enquiry, opened) ? total + 1 : total),
        0
      ),
    [enquiries, opened]
  );
}

export default useNewEnquiryCount;
